export type UrlType = 'youtube' | 'soundcloud' | 'bandcamp' | 'instagram' | 'vimeo' | 'website' | 'linktree' | 'iframe';

export interface ProcessedUrl {
    type: UrlType;
    url: string;
    embedUrl?: string;
    displayName?: string;
    iframeHtml?: string;
}

export function getUrlType(url: string): UrlType {
    if (url.trim().startsWith('<iframe') && url.trim().includes('</iframe>')) {
        return 'iframe';
    }
    if (url.includes('youtube.com') || url.includes('youtu.be')) {
        return 'youtube';
    }
    if (url.includes('soundcloud.com')) {
        return 'soundcloud';
    }
    if (url.includes('bandcamp.com')) {
        return 'bandcamp';
    }
    if (url.includes('instagram.com')) {
        return 'instagram';
    }
    if (url.includes('vimeo.com')) {
        return 'vimeo';
    }
    if (url.includes('linktr.ee')) {
        return 'linktree';
    }
    return 'website';
}

export function processUrl(url: string): ProcessedUrl {
    const type = getUrlType(url);

    switch (type) {
        case 'iframe': {
            return {
                type,
                url,
                iframeHtml: url,
                displayName: 'Embedded Content'
            };
        }

        case 'youtube': {
            // Extract video ID or playlist ID from URL
            const playlistMatch = url.match(/[?&]list=([^&]+)/);
            const videoMatch = url.match(/(?:v=|\/)([\w-]{11})(?:\?|&|$)/);

            const playlistId = playlistMatch ? playlistMatch[1] : null;
            const videoId = videoMatch ? videoMatch[1] : null;

            const embedUrl = playlistId
                ? `https://www.youtube.com/embed/videoseries?list=${playlistId}`
                : videoId
                ? `https://www.youtube.com/embed/${videoId}`
                : url;

            return {
                type,
                url,
                embedUrl,
                displayName: 'Watch on YouTube'
            };
        }

        case 'vimeo': {
            const videoIdMatch = url.match(/vimeo\.com\/(\d+)/);
            const videoId = videoIdMatch ? videoIdMatch[1] : null;
            const embedUrl = videoId ? `https://player.vimeo.com/video/${videoId}` : url;

            return {
                type,
                url,
                embedUrl,
                displayName: 'Watch on Vimeo'
            };
        }

        case 'soundcloud': {
            return {
                type,
                url,
                displayName: 'Listen on SoundCloud'
            };
        }

        case 'bandcamp': {
            return {
                type,
                url,
                displayName: 'Listen on Bandcamp'
            };
        }

        case 'instagram': {
            return {
                type,
                url,
                displayName: 'View on Instagram'
            };
        }

        case 'linktree': {
            return {
                type,
                url,
                displayName: 'View Links'
            };
        }

        case 'website':
        default: {
            return {
                type,
                url,
                displayName: 'Visit Website'
            };
        }
    }
}

export function processUrls(urls: string[]): ProcessedUrl[] {
    return urls.map(processUrl);
}